import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ArrowRight } from 'lucide-react';
const PAGE_URL = 'https://adora.media/blog/nfc-smart-link';
const HERO_IMAGE_URL = 'https://adora.media/blog/nfc-smart-link.png';
const HERO_IMAGE_WIDTH = 1200;
const HERO_IMAGE_HEIGHT = 800;
const HERO_IMAGE_ALT = 'Infographic: one NFC keychain tapped against a phone showing a chooser landing page with Facebook, Instagram, TikTok, menu, location, Telegram, and today’s promotion. Side panels contrast the problem (fixed link, hard to change, no tracking) with the solution (one smart link, update anytime, real analytics).';
const PUBLISHED_AT = '2026-05-20';
const TITLE = "Why you shouldn't put a fixed link directly on your NFC tag — Adora";
const DESCRIPTION = 'Writing a Facebook page or menu URL straight onto an NFC tag locks you in: promotions end, platforms change, and you can never reuse the tag. A smart link keeps the chip permanent, lets the destination change in software, and makes every tap measurable.';
const KEYWORDS = 'NFC tag, NFC smart link, programmable NFC, rewrite NFC tag, NFC marketing, NFC keychain, NFC for restaurants, smart link, QR vs NFC, offline marketing, campaign tracking, Adora Analytics';
const FAQS = [
    {
        question: 'Can I change what an NFC tag points to without re-tapping every chip?',
        answer: 'Only if the chip was written with a smart link — a single durable URL whose destination lives in software. If the chip was written with a final URL like facebook.com/your-page, you have to physically re-encode every tag, or replace it. Smart links solve this by separating the printed pointer from the destination it serves.',
    },
    {
        question: 'What is a smart link in the context of NFC?',
        answer: 'A smart link is a single, durable URL whose destination is configurable from a dashboard. The NFC chip stores that URL once and never has to change again. The destination behind it can be a chooser landing page, an OS-aware app store redirect, a feedback form, or a product gallery, and can be swapped at any time without touching the chip.',
    },
    {
        question: 'Are most NFC tags rewritable? Why use a smart link if they are?',
        answer: 'Many NFC tags are rewritable (NTAG213/215/216), but rewriting a tag still requires physical access. With 5 tags that is trivial. With 500 keychains across 12 outlets, it is a project. A smart link makes the destination changeable without touching the hardware, so the same chip works for the lifetime of the device.',
    },
    {
        question: 'Can one NFC tag offer multiple actions — Facebook, TikTok, menu, location?',
        answer: 'Yes. Behind the smart link, run an Adora campaign in classic mode with multiple destinations (one per action). The customer taps the tag, sees a chooser, and picks. Each tap is recorded once; the click that follows attaches the chosen destination to that scan, so you can see exactly which action each tap converts to.',
    },
    {
        question: 'How do I see how many people tapped a specific physical NFC tag?',
        answer: 'Give each physical placement its own Smart Link inside the same campaign — one for the keychain, one for the counter sticker, one for the table card. Every tap is recorded against that Smart Link, along with device, browser, and approximate location. The dashboard then breaks scans down by Smart Link so you can see which surface earns the tap.',
    },
    {
        question: 'Does using a smart link slow down the redirect compared to a direct URL?',
        answer: 'In practice, no. The smart link adds one HTTPS request to your domain before the chosen destination loads — typically under 200ms on a modern phone. For chooser pages there is no extra hop at all because the chooser is the page. The flexibility and analytics are worth far more than the latency cost.',
    },
    {
        question: 'Can I print a QR code alongside the NFC tag so non-NFC phones still work?',
        answer: 'Yes, and you generally should. Point the QR code at the same Smart Link. NFC handles the tap, QR handles cameras, and both attribute scans to the same campaign so your analytics stay unified. Adora generates the QR for any Smart Link with one click.',
    },
];
const ARTICLE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: "Why you shouldn't put a fixed link directly on your NFC tag",
    description: DESCRIPTION,
    url: PAGE_URL,
    mainEntityOfPage: PAGE_URL,
    inLanguage: 'en',
    image: {
        '@type': 'ImageObject',
        url: HERO_IMAGE_URL,
        width: HERO_IMAGE_WIDTH,
        height: HERO_IMAGE_HEIGHT,
    },
    datePublished: PUBLISHED_AT,
    dateModified: PUBLISHED_AT,
    author: {
        '@type': 'Organization',
        name: 'Adora Analytics',
        url: 'https://adora.media',
    },
    publisher: {
        '@type': 'Organization',
        name: 'Adora Analytics',
        url: 'https://adora.media',
        logo: {
            '@type': 'ImageObject',
            url: 'https://adora.media/icons/icon-512x512.png',
        },
    },
    about: [
        { '@type': 'Thing', name: 'NFC tags' },
        { '@type': 'Thing', name: 'Smart Links' },
        { '@type': 'Thing', name: 'Offline marketing analytics' },
        { '@type': 'Thing', name: 'Campaign tracking' },
    ],
    keywords: KEYWORDS,
};
const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
};
export const Route = createFileRoute('/_layout/blog/nfc-smart-link/')({
    component: NfcSmartLinkPost,
    head: () => ({
        meta: [
            { title: TITLE },
            { name: 'description', content: DESCRIPTION },
            { name: 'keywords', content: KEYWORDS },
            { name: 'author', content: 'Adora Analytics' },
            { name: 'robots', content: 'index, follow, max-image-preview:large' },
            { property: 'og:type', content: 'article' },
            { property: 'og:url', content: PAGE_URL },
            { property: 'og:title', content: TITLE },
            { property: 'og:description', content: DESCRIPTION },
            { property: 'og:site_name', content: 'Adora Analytics' },
            { property: 'og:image', content: HERO_IMAGE_URL },
            { property: 'og:image:width', content: String(HERO_IMAGE_WIDTH) },
            { property: 'og:image:height', content: String(HERO_IMAGE_HEIGHT) },
            { property: 'og:image:alt', content: HERO_IMAGE_ALT },
            { property: 'article:published_time', content: `${PUBLISHED_AT}T00:00:00Z` },
            { property: 'article:modified_time', content: `${PUBLISHED_AT}T00:00:00Z` },
            { property: 'article:author', content: 'Adora Analytics' },
            { property: 'article:section', content: 'Guide' },
            { property: 'article:tag', content: 'NFC' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: TITLE },
            { name: 'twitter:description', content: DESCRIPTION },
            { name: 'twitter:image', content: HERO_IMAGE_URL },
            { name: 'twitter:image:alt', content: HERO_IMAGE_ALT },
        ],
        links: [{ rel: 'canonical', href: PAGE_URL }],
    }),
});
function NfcSmartLinkPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            {/* Schema.org JSON-LD — feeds Google rich results + AI Overviews
            (ChatGPT Search, Perplexity, Gemini, Claude). */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ARTICLE_SCHEMA) }}/>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}/>

            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">Guide · 2026-05-20</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    Why you shouldn&apos;t put a fixed link directly on your NFC tag
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Writing a Facebook page or menu URL straight onto an NFC tag feels efficient — until the promotion
                    ends, the platform changes, or you want to know which tag actually worked.
                </p>
            </header>

            <figure className="mt-8 overflow-hidden rounded-xl border bg-muted/30">
                <img src={HERO_IMAGE_URL} alt={HERO_IMAGE_ALT} width={HERO_IMAGE_WIDTH} height={HERO_IMAGE_HEIGHT} loading="eager" fetchPriority="high" decoding="async" className="h-auto w-full"/>
                <figcaption className="px-4 py-3 text-xs text-muted-foreground">
                    One NFC tag, many destinations — and a dashboard that tells you which one each tap chose.
                </figcaption>
            </figure>

            {/* TL;DR card — written for AI Overview / Perplexity / ChatGPT extraction. */}
            <section aria-labelledby="tldr" className="mt-8">
                <Card className="border-primary/30">
                    <CardHeader>
                        <CardTitle id="tldr" className="text-base uppercase tracking-wider text-primary">
                            The short answer
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-base leading-relaxed space-y-3">
                        <p>
                            Don&apos;t write your Facebook URL, menu URL, or promo URL directly onto an NFC tag. Write a{' '}
                            <strong>smart link</strong> instead — a single durable URL whose destination you control in
                            software.
                        </p>
                        <p>
                            That way the chip stays the same when your campaign changes, the keychain still works next
                            year, and every tap is attributable to a specific physical surface.
                        </p>
                    </CardContent>
                </Card>
            </section>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <p>
                    NFC tags are turning up everywhere small businesses operate: keychains, table cards, counter
                    stickers, packaging labels, posters, business cards. A customer taps the tag with their phone and a
                    link opens instantly. It feels like magic, and the temptation is to write the obvious URL straight
                    onto the chip — your Facebook page, your Instagram, the menu, the current promotion.
                </p>
                <p>That shortcut quietly costs you twice: in flexibility, and in everything you could have learned.</p>

                <h2>What happens the day the link changes</h2>
                <p>
                    A fixed URL written onto an NFC tag is the digital equivalent of carving a phone number into your
                    front door. It looks fine on day one. Then reality moves on:
                </p>
                <ul>
                    <li>The Facebook page you wrote on the tag becomes secondary to TikTok.</li>
                    <li>The lunch promotion ends; you now have 200 keychains pointing at an expired campaign.</li>
                    <li>You restructure your website and the menu URL 404s.</li>
                    <li>You change brands, payment provider, or domain.</li>
                </ul>
                <p>
                    Every one of those is an afternoon of rewriting tags, reprinting stickers, recalling keychains, or —
                    more often — quietly accepting that the tags are dead and pretending they never existed.
                </p>

                <h2>The better way: one smart link behind the tag</h2>
                <p>
                    Write a <strong>smart link</strong> onto the tag once, and treat the tag as a permanent entry point.
                    The destination behind that link lives in software, where it&apos;s easy to change.
                </p>
                <p>The same physical NFC tag can route to:</p>
                <ul>
                    <li>Your Facebook page this week.</li>
                    <li>A TikTok video next week.</li>
                    <li>Today&apos;s lunch promotion at noon, a different one at dinner.</li>
                    <li>A Telegram chat during a campaign.</li>
                    <li>A landing page with multiple actions when you want the customer to choose.</li>
                </ul>
                <p>The tag is hardware; the destination is software. Don&apos;t glue them together.</p>

                <h2>Where the cost saving actually comes from</h2>
                <p>
                    NFC tags are cheap. The expensive part is everything <em>around</em> them: the printing, the
                    placement, the keychains in 50 outlets, the packaging run you already committed to. A smart link
                    means you pay that cost once.
                </p>
                <p>You avoid:</p>
                <ul>
                    <li>Reprogramming tags one by one when a URL changes.</li>
                    <li>Reprinting QR variants of the same tag.</li>
                    <li>Throwing away packaging because the campaign expired.</li>
                    <li>The silent worst case — customers tapping a dead link and never coming back.</li>
                </ul>

                <h2>Where Adora goes further than a normal short link</h2>
                <p>
                    A plain URL shortener already gives you the redirect-from-software part. What it doesn&apos;t give
                    you is <strong>understanding</strong>.
                </p>
                <p>
                    In Adora, the link on your tag is a <strong>Smart Link</strong> attached to a{' '}
                    <strong>campaign</strong>. Every tap creates a scan record. If the customer then chooses an action —
                    Facebook, TikTok, menu, location, Telegram — Adora attaches that destination to the same scan
                    record. One tap, at most one attributed click, fully connected.
                </p>
                <p>That means you can answer questions a shortener can&apos;t:</p>
                <ul>
                    <li>How many people tapped this specific keychain, this week, in this branch?</li>
                    <li>Which placement is converting — table card, packaging, or business card?</li>
                    <li>After tapping, do customers prefer Facebook, TikTok, or the menu?</li>
                    <li>Which branch&apos;s tags are dead, and which are pulling weight?</li>
                </ul>
                <p>
                    Marketing isn&apos;t just about sending people somewhere. It&apos;s about learning what they chose
                    once they got there.
                </p>

                <h2>Worked example: one NFC keychain at a restaurant</h2>
                <p>
                    Imagine a restaurant hands out NFC keychains. The tag is written once with an Adora Smart Link. The
                    landing page behind it offers:
                </p>
                <ul>
                    <li>View menu</li>
                    <li>Follow on Facebook</li>
                    <li>Watch on TikTok</li>
                    <li>Open location in Maps</li>
                    <li>Chat on Telegram</li>
                    <li>See today&apos;s promotion</li>
                </ul>
                <p>
                    Next month the promotion changes. The keychain doesn&apos;t. Three months in, the dashboard shows
                    that customers tap often but overwhelmingly go to the menu and TikTok — almost nobody uses
                    Instagram. So the restaurant drops Instagram from the landing and replaces it with a 1-tap feedback
                    form. The same keychain keeps working, but now it does more useful work.
                </p>
                <p>
                    That feedback loop — placement → tap → choice → adjust — is what turns NFC from a parlour trick into
                    a marketing tool.
                </p>

                <h2>Normal NFC vs. smart NFC</h2>
                <p>A normal NFC tag sends a customer to one fixed place, and tells you nothing afterwards.</p>
                <p>
                    A smart NFC tag — one Smart Link, one campaign, multiple destinations — lets the page behind it
                    evolve with your business, and tells you which destination is actually earning the tap.
                </p>
                <p>
                    A normal tag is a shortcut. A smart tag is a measurement instrument that happens to also be a
                    shortcut.
                </p>

                <h2>What to write on the tag, then</h2>
                <p>
                    Don&apos;t write <code>https://facebook.com/your-page</code> on the chip. Don&apos;t write your menu
                    URL. Don&apos;t write the promotion of the week. Write the Adora Smart Link that represents this
                    physical surface — this keychain, this table, this packaging run — and let the destination move
                    underneath.
                </p>
                <p>
                    One tag. One smart link. Multiple actions. Real analytics. And the same chip still working a year
                    from now when everything about your campaigns has changed.
                </p>
            </div>

            {/* FAQ — visible accordion mirrored by FAQPage JSON-LD above so the
            answers are extractable by Google AI Overviews and chat search. */}
            <section aria-labelledby="faq" className="mt-12">
                <h2 id="faq" className="text-2xl font-semibold tracking-tight">
                    Frequently asked questions
                </h2>
                <Accordion type="single" collapsible className="mt-4">
                    {FAQS.map((f, i) => (<AccordionItem key={f.question} value={`faq-${i}`}>
                            <AccordionTrigger className="text-left">{f.question}</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground leading-relaxed">
                                {f.answer}
                            </AccordionContent>
                        </AccordionItem>))}
                </Accordion>
            </section>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    Try Adora free
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
